import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PdfViewerComponent, PdfViewerModule } from 'ng2-pdf-viewer';

@Component({
  selector: 'sia-base-pdf-viewer',
  templateUrl: './sia-base-pdf-viewer.html',
  styleUrls: ['./sia-base-pdf-viewer.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, PdfViewerModule, MatButtonModule, MatIconButton, MatProgressSpinner, DecimalPipe],
})
export class SiaBasePdfViewerComponent implements OnDestroy {
  @ViewChild(PdfViewerComponent) pdfComponent!: PdfViewerComponent;

  #cdr = inject(ChangeDetectorRef);

  private _file: Blob | File | null = null;
  readonly pdfSrc = signal<Uint8Array | null>(null);
  readonly loading = signal<boolean>(false);
  readonly error = signal<string | null>(null);
  readonly zoomLevel = signal<number>(1);
  readonly ready = signal<boolean>(false);

  @Input() initialZoom: number = 1;

  @Output() matchesCountChange = new EventEmitter<{
    current: number;
    total: number;
  }>();

  /**
   * Emesso la prima volta che l'utente raggiunge l'ultima pagina del PDF
   * (scroll fino alla fine). Si resetta automaticamente quando viene caricato
   * un nuovo file.
   */
  @Output() scrolledToEnd = new EventEmitter<void>();

  private eventBusSubscribed = false;
  private scrollEventBusSubscribed = false;
  private totalPages = 0;
  private reachedEnd = false;

  // Riferimenti stabili alle callback registrate sull'eventBus di pdf.js.
  // Vengono conservati in campi privati per poter essere rimossi in
  // `ngOnDestroy` / durante il reload del file, evitando memory leak quando
  // il componente viene distrutto e ricreato (es. dialog riaperto piu volte).
  #onPageChanging = (data: any): void => {
    if (this.reachedEnd || !this.totalPages) {
      return;
    }
    const pageNumber: number | undefined = data?.pageNumber;
    if (pageNumber && pageNumber >= this.totalPages) {
      this.reachedEnd = true;
      this.scrolledToEnd.emit();
    }
  };

  #onPagesLoaded = (data: any): void => {
    if (typeof data?.pagesCount === 'number') {
      this.totalPages = data.pagesCount;
      if (this.totalPages === 1 && !this.reachedEnd) {
        this.reachedEnd = true;
        this.scrolledToEnd.emit();
      }
    }
  };

  #onUpdateFindMatchesCount = (data: any): void => {
    if (data && data.matchesCount) {
      this.matchesCountChange.emit(data.matchesCount);
    }
  };

  #onUpdateFindControlState = (data: any): void => {
    if (data && data.matchesCount) {
      this.matchesCountChange.emit(data.matchesCount);
    }
  };

  get file(): Blob | File | null {
    return this._file;
  }

  @Input() set file(value: Blob | File | null | undefined) {
    if (!value) {
      return;
    }
    // Se il componente viene riutilizzato per un nuovo file, rimuoviamo
    // le subscription sull'eventBus del documento precedente prima di
    // resettare lo stato (evita che i vecchi handler restino attivi).
    this.#unsubscribeEventBus();
    this._file = value;
    this.pdfSrc.set(null);
    this.error.set(null);
    this.loading.set(true);
    // Reset dello stato di scroll-to-end per il nuovo documento
    this.reachedEnd = false;
    this.totalPages = 0;

    // Verifica che sia un Blob o File valido
    if (!(value instanceof Blob)) {
      this.error.set('Il file fornito non è un Blob o File valido');
      this.loading.set(false);
      return;
    }

    // Verifica il tipo MIME (se disponibile)
    // Accettiamo anche application/octet-stream perche i file PDF archiviati
    // senza estensione nota vengono spesso serviti con questo MIME generico.
    const acceptedTypes = ['application/pdf', 'application/octet-stream'];
    if (value.type && !acceptedTypes.includes(value.type)) {
      this.error.set(`Tipo file non supportato: ${value.type}. Atteso: application/pdf`);
      this.loading.set(false);
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = () => {
      this.pdfSrc.set(new Uint8Array(fileReader.result as ArrayBuffer));
      this.loading.set(false);
      this.#cdr.detectChanges();
    };
    fileReader.onerror = () => {
      this.error.set('Errore durante la lettura del file PDF');
      this.loading.set(false);
      this.#cdr.detectChanges();
    };
    fileReader.readAsArrayBuffer(value);
  }

  ngAfterViewInit(): void {
    this.zoomLevel.set(this.initialZoom);
    this.ready.set(true);
  }

  /**
   * Callback invocato dall'output `(pages-initialized)` del `<pdf-viewer>`:
   * a questo punto l'eventBus e pronto e conosciamo il numero di pagine.
   * Registra (una sola volta) i listener sull'eventBus per rilevare il
   * raggiungimento dell'ultima pagina e, nel caso di PDF monopagina,
   * emette subito `scrolledToEnd`.
   */
  onPagesInitialized(event: { pagesCount?: number } | any): void {
    const viewer = (this.pdfComponent as any)?.pdfViewer;
    const pagesCount: number = event?.pagesCount ?? viewer?.pagesCount ?? 0;
    this.totalPages = pagesCount;
    this.subscribeScrollTracking();

    // PDF con una sola pagina: la fine e gia visibile all'apertura.
    if (this.totalPages === 1 && !this.reachedEnd) {
      this.reachedEnd = true;
      this.scrolledToEnd.emit();
    }
  }

  private subscribeScrollTracking(): void {
    if (this.scrollEventBusSubscribed) {
      return;
    }
    if (!this.pdfComponent || !this.pdfComponent.eventBus) {
      return;
    }
    this.scrollEventBusSubscribed = true;

    // `pagechanging` scatta quando la pagina corrente (piu visibile) cambia.
    // Quando coincide con l'ultima pagina, consideriamo raggiunta la fine.
    this.pdfComponent.eventBus.on('pagechanging', this.#onPageChanging);

    // Aggiornamento del numero totale di pagine se cambia dopo l'init.
    this.pdfComponent.eventBus.on('pagesloaded', this.#onPagesLoaded);
  }

  zoomIn() {
    this.zoomLevel.update((z) => Math.min(z + 0.25, 3));
  }

  zoomOut() {
    this.zoomLevel.update((z) => Math.max(z - 0.25, 0.5));
  }

  resetZoom() {
    this.zoomLevel.set(1);
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    if (event.ctrlKey) {
      event.preventDefault();
      if (event.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    }
  }

  search(query: string, type: 'find' | 'findagain' = 'find', previous: boolean = false) {
    if (this.pdfComponent && this.pdfComponent.eventBus) {
      if (!this.eventBusSubscribed) {
        this.eventBusSubscribed = true;
        this.pdfComponent.eventBus.on('updatefindmatchescount', this.#onUpdateFindMatchesCount);
        this.pdfComponent.eventBus.on('updatefindcontrolstate', this.#onUpdateFindControlState);
      }
      if (query === '') {
        this.pdfComponent.eventBus.dispatch('find', {
          source: this.pdfComponent,
          type: '',
          query: '',
          caseSensitive: false,
          findPrevious: false,
          highlightAll: false,
          phraseSearch: true,
        });
        return;
      }
      this.pdfComponent.eventBus.dispatch('find', {
        source: this.pdfComponent,
        type: type === 'find' ? '' : 'again',
        query: query,
        caseSensitive: false,
        findPrevious: previous,
        highlightAll: true,
        phraseSearch: true,
      });
    }
  }

  ngOnDestroy(): void {
    this.#unsubscribeEventBus();
  }

  /**
   * Rimuove tutti gli handler registrati sull'eventBus di pdf.js e resetta
   * i flag di sottoscrizione. Idempotente: se nessuna subscription e attiva
   * o l'eventBus non e disponibile, non fa nulla. Chiamato sia da
   * `ngOnDestroy` sia dal setter `file` (prima di caricare un nuovo documento
   * sulla stessa istanza del componente).
   */
  #unsubscribeEventBus(): void {
    const eventBus = this.pdfComponent?.eventBus;
    if (!eventBus) {
      // Resetta comunque i flag per coerenza: se l'eventBus non esiste piu,
      // non abbiamo piu subscription valide.
      this.scrollEventBusSubscribed = false;
      this.eventBusSubscribed = false;
      return;
    }

    if (this.scrollEventBusSubscribed) {
      eventBus.off('pagechanging', this.#onPageChanging);
      eventBus.off('pagesloaded', this.#onPagesLoaded);
      this.scrollEventBusSubscribed = false;
    }

    if (this.eventBusSubscribed) {
      eventBus.off('updatefindmatchescount', this.#onUpdateFindMatchesCount);
      eventBus.off('updatefindcontrolstate', this.#onUpdateFindControlState);
      this.eventBusSubscribed = false;
    }
  }
}
